import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ThumbsUp } from "lucide-react";
import { Layout } from "../components/Layout";
import { getIcon } from "../lib/icons";
import { PageRocket } from "../components/PageRocket";
import { fetchCategories, voteCategory } from "../lib/api";

export default function Categories() {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-[#0F0F0F]">
              Categories
            </h1>
            <p className="text-[#687280] mt-3 max-w-xl">
              Browse business ideas by industry. Every category is a doorway to your next big idea.
            </p>
          </div>
          <PageRocket className="w-36 lg:w-52 -mt-4" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-12">
          {categories.map((c, i) => (
            <div key={c.slug} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
              <CategoryVoteCard category={c} />
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}

const CategoryVoteCard = ({ category }) => {
  const navigate = useNavigate();
  const Icon = getIcon(category.icon);
  const [popped, setPopped] = useState(false);

  const vote = (e) => {
    e.stopPropagation();
    setPopped(true);
    setTimeout(() => setPopped(false), 320);
    voteCategory(category.slug).catch(() => {});
  };

  return (
    <div
      onClick={() => navigate(`/categories/${category.slug}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/categories/${category.slug}`)}
      className="relative bg-white rounded-2xl p-7 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 h-full flex flex-col cursor-pointer text-left"
      data-testid={`category-card-${category.slug}`}
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
        style={{ backgroundColor: `${category.color}18`, color: category.color }}
      >
        <Icon size={26} strokeWidth={1.75} />
      </div>
      <h3 className="font-display font-semibold text-lg text-[#0F0F0F] mb-2">{category.name}</h3>
      <p className="text-sm text-[#687280] leading-relaxed mb-4 flex-1">{category.description}</p>
      <span className="text-xs font-medium text-[#D4AF37] pr-12">
        {category.count > 0 ? `${category.count} Ideas →` : "Coming soon →"}
      </span>

      <button
        onClick={vote}
        aria-label={`Upvote ${category.name}`}
        title="Upvote this category"
        className={`absolute bottom-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
          popped
            ? "bg-[#D4AF37] text-white scale-125"
            : "bg-[#F7F7F7] text-[#687280] hover:bg-[#D4AF37] hover:text-white active:scale-110"
        }`}
        data-testid={`category-vote-btn-${category.slug}`}
      >
        <ThumbsUp size={16} />
      </button>
    </div>
  );
};
